import { Injectable, computed, signal } from '@angular/core';
import { Permission } from '@interfaces/a-organizar/permission';
import { buildPermissionTree } from '@app/features/dashboard/group-register/permission-tree/permission-tree.builder';
import {
  PermissionChange,
  PermissionModuleNode,
  PermissionPreset,
  PermissionScreenNode,
  PermissionTreeNode,
} from '@app/features/dashboard/group-register/permission-tree/permission-tree.models';
import {
  SearchResultScreen,
  applyPreset,
  diffSelection,
  filterTree,
  indexActions,
  screensOf,
  searchActions,
  setValues,
  toggleNode,
} from '@app/features/dashboard/group-register/permission-tree/permission-selection.utils';

/**
 * Aviso depois de ligar muita coisa de uma vez com gravação junto (quadrado de módulo ou área, "Tudo",
 * "Marcar os N"): diz quanto ligou e deixa desfazer só aquele clique.
 */
export interface BulkChangeNotice {
  /** Onde: "Controle", "Endividamento", "os resultados da busca". */
  scope: string;
  added: number;
  writeAdded: number;
  /** Seleção de antes do clique. */
  previous: ReadonlySet<string>;
}

export interface PermissionSummary {
  /** Claims marcadas que existem no catálogo do papel. */
  permissions: number;
  /** Módulos com ao menos uma claim marcada (rótulos, na ordem da árvore). */
  modules: string[];
  /** Telas com "Aparece no menu" ligado. */
  menuScreens: number;
}

/**
 * Estado da seleção de permissões da tela de grupo (card 3902). Um por tela (`providers` do componente).
 *
 * A seleção é o conjunto de claims que vai no payload. Claims que vieram do grupo e não estão no catálogo do papel
 * (legado) ficam na seleção e são enviadas de volta como estavam: a tela nunca apaga o que não mostra.
 */
@Injectable()
export class GroupPermissionsStateService {
  readonly modules = signal<PermissionModuleNode[]>([]);
  readonly selected = signal<ReadonlySet<string>>(new Set());
  readonly initial = signal<ReadonlySet<string>>(new Set());
  readonly activeNodeId = signal<string | null>(null);
  readonly expanded = signal<ReadonlySet<string>>(new Set());
  readonly query = signal('');
  readonly onlySelected = signal(false);
  readonly bulkNotice = signal<BulkChangeNotice | null>(null);

  readonly index = computed(() => indexActions(this.modules()));

  private readonly nodesById = computed(() => {
    const map = new Map<string, PermissionTreeNode>();
    this.modules().forEach(module => {
      map.set(module.id, module);
      module.children.forEach(child => {
        map.set(child.id, child);
        if (child.kind === 'area') child.screens.forEach(item => map.set(item.id, item));
      });
    });
    return map;
  });

  readonly activeNode = computed(() => {
    const id = this.activeNodeId();
    return id ? (this.nodesById().get(id) ?? null) : null;
  });

  readonly isSearching = computed(() => this.query().trim().length > 0);

  readonly searchResults = computed<SearchResultScreen[]>(() =>
    searchActions(this.modules(), this.query())
  );

  /** Árvore que a coluna da esquerda mostra: inteira, só com resultados da busca ou só com o que está marcado. */
  readonly visibleTree = computed(() => {
    if (this.isSearching()) {
      const results = new Map(
        this.searchResults().map(result => [result.screen.id, result.actions.map(a => a.value)])
      );
      return filterTree(this.modules(), screen => results.get(screen.id) ?? null);
    }
    if (this.onlySelected()) {
      const selected = this.selected();
      return filterTree(this.modules(), screen => {
        const values = screen.values.filter(value => selected.has(value));
        return values.length ? screen.values : null;
      });
    }
    return this.modules();
  });

  readonly searchResultCount = computed(() =>
    this.searchResults().reduce((total, result) => total + result.actions.length, 0)
  );

  readonly changes = computed<PermissionChange[]>(() =>
    diffSelection(this.initial(), this.selected(), this.index())
  );

  readonly summary = computed<PermissionSummary>(() => {
    const selected = this.selected();
    const index = this.index();
    const permissions = [...selected].filter(value => index.has(value)).length;
    const modules = this.modules()
      .filter(module => module.values.some(value => selected.has(value)))
      .map(module => module.label);
    const menuScreens = this.modules()
      .flatMap(module => screensOf(module))
      .filter(screen => !!screen.menuAction && selected.has(screen.menuAction.value)).length;
    return { permissions, modules, menuScreens };
  });

  /** Claims marcadas que o catálogo do papel não traz (vêm do grupo salvo e são mantidas). */
  readonly unknownSelectedCount = computed(() => {
    const index = this.index();
    return [...this.selected()].filter(value => !index.has(value)).length;
  });

  readonly hasSelection = computed(() => this.selected().size > 0);

  /** Carrega o catálogo do papel e abre a primeira tela com algo marcado (ou a primeira da árvore). */
  loadCatalog(catalog: Permission[]): void {
    const modules = buildPermissionTree(catalog);
    this.modules.set(modules);
    this.query.set('');

    const selected = this.selected();
    const screens = modules.flatMap(module => screensOf(module));
    const first =
      screens.find(item => item.values.some(value => selected.has(value))) ?? screens[0];
    this.activeNodeId.set(first?.id ?? null);
    this.expanded.set(new Set(first ? this.ancestorIds(first) : []));
  }

  /** Seleção vinda do grupo salvo; vira também a base do "o que muda ao salvar". */
  setInitialSelection(values: string[]): void {
    const set = new Set(values);
    this.initial.set(set);
    this.selected.set(new Set(set));
  }

  /** Troca de papel no cadastro: catálogo novo, nada marcado. */
  reset(): void {
    this.modules.set([]);
    this.initial.set(new Set());
    this.selected.set(new Set());
    this.activeNodeId.set(null);
    this.expanded.set(new Set());
    this.query.set('');
    this.bulkNotice.set(null);
  }

  selectNode(node: PermissionTreeNode): void {
    this.activeNodeId.set(node.id);
    if (node.kind !== 'screen') this.setExpanded(node.id, true);
  }

  openScreen(screen: PermissionScreenNode): void {
    this.query.set('');
    this.activeNodeId.set(screen.id);
    const expanded = new Set(this.expanded());
    this.ancestorIds(screen).forEach(id => expanded.add(id));
    this.expanded.set(expanded);
  }

  toggleExpanded(id: string): void {
    this.setExpanded(id, !this.expanded().has(id));
  }

  collapseAll(): void {
    this.expanded.set(new Set());
  }

  toggleNodeSelection(node: PermissionTreeNode): void {
    this.commit(toggleNode(this.selected(), node), node.kind === 'screen' ? null : node.label);
  }

  setValue(value: string, checked: boolean): void {
    this.commit(setValues(this.selected(), [value], checked));
  }

  setMany(values: string[], checked: boolean): void {
    this.commit(setValues(this.selected(), values, checked), 'os resultados da busca');
  }

  applyPreset(node: PermissionTreeNode, preset: PermissionPreset): void {
    this.commit(applyPreset(this.selected(), node, preset), preset === 'all' ? node.label : null);
  }

  undoAll(): void {
    this.commit(new Set(this.initial()));
  }

  /** Desfaz uma linha do "o que muda ao salvar". */
  undoValue(value: string): void {
    this.commit(setValues(this.selected(), [value], this.initial().has(value)));
  }

  /** Volta a seleção de antes do último clique em massa. */
  undoBulk(): void {
    const notice = this.bulkNotice();
    if (!notice) return;
    this.selected.set(new Set(notice.previous));
    this.bulkNotice.set(null);
  }

  dismissBulkNotice(): void {
    this.bulkNotice.set(null);
  }

  /**
   * Grava a seleção nova. `bulkScope` (só nos cliques em massa) liga o aviso quando o clique liga mais de uma
   * permissão e alguma delas é de gravação; qualquer outra mudança tira o aviso, para o "Desfazer" dele nunca
   * desfazer mais do que aquele clique.
   */
  private commit(next: Set<string>, bulkScope: string | null = null): void {
    const previous = this.selected();
    this.selected.set(next);
    if (!bulkScope) {
      this.bulkNotice.set(null);
      return;
    }
    const index = this.index();
    const added = [...next].filter(value => !previous.has(value));
    const writeAdded = added.filter(value => index.get(value)?.action.kind === 'write').length;
    this.bulkNotice.set(
      added.length > 1 && writeAdded > 0
        ? { scope: bulkScope, added: added.length, writeAdded, previous }
        : null
    );
  }

  /** Telas que passam a ter gravação ligada ao salvar (para a confirmação do Salvar). */
  readonly screensGainingWrite = computed(
    () =>
      new Set(
        this.changes()
          .filter(change => change.type === 'added' && change.kind === 'write')
          .map(change => change.screenId)
      ).size
  );

  /** Payload: tudo o que está marcado, inclusive claims antigas fora do catálogo. */
  payload(): string[] {
    return [...this.selected()];
  }

  private setExpanded(id: string, open: boolean): void {
    const next = new Set(this.expanded());
    if (open) next.add(id);
    else next.delete(id);
    this.expanded.set(next);
  }

  private ancestorIds(screen: PermissionScreenNode): string[] {
    for (const module of this.modules()) {
      for (const child of module.children) {
        if (child.id === screen.id) return [module.id];
        if (child.kind === 'area' && child.screens.some(item => item.id === screen.id)) {
          return [module.id, child.id];
        }
      }
    }
    return [];
  }
}
